import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const organizations = pgTable("organizations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  logo: text("logo"),
  brandColor: text("brand_color").default("#0c3547"),
  openaiApiKey: text("openai_api_key"),
  detectedModels: text("detected_models"),
  plan: text("plan").default("starter"),
  botLimit: integer("bot_limit").default(3),
  agentLimit: integer("agent_limit").default(2),
  monthlyMessageLimit: integer("monthly_message_limit").default(1000),
  messagesUsed: integer("messages_used").default(0),
  dataRetentionDays: integer("data_retention_days").default(90),
  landingPageConfig: text("landing_page_config"),
  mode: text("mode").default("public"),
  ssoConfig: text("sso_config"),
  ssoEnforced: text("sso_enforced").default("false"),
  // Stripe billing — populated when the Master Admin subscribes this
  // tenant to a plan via Stripe Checkout. `stripeCustomerId` is
  // long-lived; `stripeSubscriptionId` may be null until the customer
  // completes checkout, and is cleared by the webhook on cancellation.
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  // Mirrored copy of the synced subscription's status
  // (`active|trialing|past_due|canceled|incomplete|...`) so the
  // Tenants tab can show a badge without joining stripe.subscriptions.
  subscriptionStatus: text("subscription_status"),
  // Institutional Voice & Inclusivity profile (RFP compliance). When enabled
  // these standards are injected into every chatbot response. Blank fields
  // fall back to built-in institutional defaults server-side.
  voiceProfileEnabled: boolean("voice_profile_enabled").default(true),
  voiceTone: text("voice_tone"),
  voicePreferredTerms: text("voice_preferred_terms"),
  voiceProhibitedTerms: text("voice_prohibited_terms"),
  voiceReadingLevel: text("voice_reading_level"),
  voiceInclusivityStandard: text("voice_inclusivity_standard"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password"),
  name: text("name").notNull(),
  role: text("role").default("user"),
  organizationId: varchar("organization_id").notNull(),
  openaiApiKey: text("openai_api_key"),
  detectedModels: text("detected_models"),
  employeeId: text("employee_id"),
  jobTitle: text("job_title"),
  department: text("department"),
  managerId: varchar("manager_id"),
  ssoProvider: text("sso_provider"),
  externalId: text("external_id"),
  active: text("active").default("true"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const departments = pgTable("departments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  externalId: text("external_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const groups = pgTable("groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  source: text("source").default("manual"),
  externalId: text("external_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const groupMembers = pgTable("group_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  groupId: varchar("group_id").notNull(),
  userId: varchar("user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const botAccessRules = pgTable("bot_access_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  allowedRoles: text("allowed_roles").array().default(sql`ARRAY[]::text[]`),
  allowedGroupIds: text("allowed_group_ids").array().default(sql`ARRAY[]::text[]`),
  allowedDepartments: text("allowed_departments").array().default(sql`ARRAY[]::text[]`),
  openToAll: text("open_to_all").default("true"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Per-organization installation record for an external messaging channel
// (Slack workspace or Microsoft Teams tenant). One row per (chatbot, type)
// pair — installing the bot to a workspace creates / updates this row.
//
// `accessToken` (Slack bot token, or Teams app password ciphertext) and
// `signingSecret` (Slack signing secret, or Teams bot app id) are stored
// here so per-tenant credentials never leak into env vars.
export const channelInstallations = pgTable("channel_installations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  // 'slack' | 'teams'
  type: text("type").notNull(),
  // Slack: team_id (T...). Teams: AAD tenant id.
  externalTeamId: text("external_team_id").notNull(),
  // Human label: Slack workspace name / Teams tenant display name.
  externalTeamName: text("external_team_name"),
  // Slack: bot_user_id (U...). Teams: bot AAD app id.
  botUserId: text("bot_user_id"),
  // Slack xoxb- token / Teams app password — encrypted-at-rest envelope
  // (see server/channels/crypto.ts). NEVER read raw; always go through
  // the storage layer which decrypts on read.
  accessToken: text("access_token").notNull(),
  // Slack signing secret OR Teams bot app id (used for Bot Framework JWT
  // audience validation). Same envelope encryption as accessToken.
  signingSecret: text("signing_secret"),
  installedByUserId: varchar("installed_by_user_id"),
  status: text("status").default("active"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  // One install per (workspace/tenant) per channel type — guarantees
  // deterministic inbound webhook routing and prevents duplicate-row
  // races during reinstall.
  uniqTypeTeam: uniqueIndex("uniq_channel_type_external_team").on(t.type, t.externalTeamId),
  // One install per (chatbot, channel type) — used by the upsert path
  // when an admin reinstalls for the same bot.
  uniqBotType: uniqueIndex("uniq_channel_chatbot_type").on(t.chatbotId, t.type),
}));

// ─────────────────────────────────────────────────────────────────────────────
// PHASE 3: Tool / Action Framework
// ─────────────────────────────────────────────────────────────────────────────
//
// `tools` — registry of every action an org has enabled (registered through
//   server/tools/registry.ts). The registry seeds rows here per-org on first
//   touch; admins toggle which bots can use each tool via `bot_tools`.
// `bot_tools` — many-to-many between chatbot and tool with per-bot overrides
//   for `requiresApproval` (so a high-trust bot can skip approval for
//   request_pto while a public-facing bot still requires it).
// `tool_invocations` — append-only audit row for every tool call. Stores the
//   user, bot, tool, JSON args, JSON result, status, and timing.
// `approval_requests` — created when a tool with requiresApproval=true fires.
//   Holds the queued invocation until a manager Approves/Rejects via Slack
//   or Teams interactive button.
export const tools = pgTable("tools", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  // Stable registry key (e.g. "request_pto"). Matches the key declared in
  // server/tools/registry.ts. Used by the LLM as the function name.
  name: text("name").notNull(),
  displayName: text("display_name").notNull(),
  description: text("description").notNull(),
  category: text("category").default("general"),
  // JSON-stringified JSON-Schema for the tool arguments (OpenAI tool format).
  argsSchema: text("args_schema").notNull(),
  // text[] of role names allowed to invoke this tool. Empty = any role.
  allowedRoles: text("allowed_roles").array().default(sql`ARRAY[]::text[]`),
  requiresApproval: text("requires_approval").default("false"),
  // null = unlimited.
  perUserPerHourLimit: integer("per_user_per_hour_limit"),
  perOrgPerHourLimit: integer("per_org_per_hour_limit"),
  isActive: text("is_active").default("true"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  uniqOrgName: uniqueIndex("uniq_tools_org_name").on(t.organizationId, t.name),
}));

export const botTools = pgTable("bot_tools", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  toolId: varchar("tool_id").notNull(),
  enabled: text("enabled").default("true"),
  // null = inherit from tools.requiresApproval.
  requiresApprovalOverride: text("requires_approval_override"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  uniqBotTool: uniqueIndex("uniq_bot_tools_bot_tool").on(t.chatbotId, t.toolId),
}));

export const toolInvocations = pgTable("tool_invocations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  toolId: varchar("tool_id").notNull(),
  toolName: text("tool_name").notNull(),
  // App user id of the invoker. May be null for unauthenticated/preview.
  userId: varchar("user_id"),
  conversationId: varchar("conversation_id"),
  argsJson: text("args_json").notNull(),
  resultJson: text("result_json"),
  // 'success' | 'error' | 'denied' | 'rate_limited' | 'pending_approval' |
  // 'rejected' | 'cancelled'
  status: text("status").notNull(),
  errorMessage: text("error_message"),
  // ms wall-clock from invoke→complete. null while pending approval.
  durationMs: integer("duration_ms"),
  approvalRequestId: varchar("approval_request_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const approvalRequests = pgTable("approval_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  toolId: varchar("tool_id").notNull(),
  toolName: text("tool_name").notNull(),
  requesterUserId: varchar("requester_user_id").notNull(),
  approverUserId: varchar("approver_user_id"),
  conversationId: varchar("conversation_id"),
  argsJson: text("args_json").notNull(),
  // 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled'
  status: text("status").default("pending"),
  // Where to post the outcome back. e.g. "slack:team:channel:thread_ts"
  callbackChannel: text("callback_channel"),
  callbackRef: text("callback_ref"),
  decisionNote: text("decision_note"),
  decidedAt: timestamp("decided_at"),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Per-organization connection to an external SaaS system (ServiceNow,
// Workday, etc.). One row per (org, type). `configJson` holds the
// connector-specific credentials and OAuth tokens, encrypted-at-rest with
// the same envelope helper used for channel installs (see
// server/channels/crypto.ts). Status is updated on every test/refresh so
// the integrations UI can show a live health pill.
export const integrations = pgTable("integrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  // 'servicenow' | 'workday' | future connector slugs.
  type: text("type").notNull(),
  // Display label, defaults to the type name. Admin can rename.
  name: text("name").notNull(),
  // 'connected' | 'error' | 'unconfigured' | 'token_expired'
  status: text("status").default("unconfigured"),
  // Convenience copy of the connector base URL so admins / health checks
  // don't have to JSON-decode configJson just to read it.
  instanceUrl: text("instance_url"),
  // Ciphertext envelope of {clientId, clientSecret, username, password,
  // accessToken, refreshToken, expiresAt, impersonate, ...}.
  configJson: text("config_json"),
  lastCheckedAt: timestamp("last_checked_at"),
  lastError: text("last_error"),
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  uniqOrgType: uniqueIndex("uniq_integrations_org_type").on(t.organizationId, t.type),
}));

export const scimTokens = pgTable("scim_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  tokenHash: text("token_hash").notNull(),
  label: text("label").notNull(),
  lastUsedAt: timestamp("last_used_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Scheduled recurring exports of the tool-invocations audit log. Each row
// captures one configured cadence (daily / weekly / monthly), a relative
// window (last N days), an optional filter set, and a delivery method
// (email recipients OR HTTPS upload to an admin-supplied storage URL,
// e.g. an S3/GCS/Azure presigned upload). The runner reuses the same
// streaming CSV pipeline as the on-demand export (iterateToolInvocations
// + iterateToolInvocationsCsvAsync + formatAuditCsvTruncationRow) so the
// file format is byte-identical to a manual download.
//
// `deliveryConfigCipher` holds the ciphertext envelope of the per-type
// config JSON (email recipient list & subject, or storage URL + method +
// optional auth header) — same AES-256-GCM helper used for channel
// installs (server/channels/crypto.ts) so request headers like
// `Authorization: Bearer …` never sit at-rest as plaintext.
export const scheduledAuditExports = pgTable("scheduled_audit_exports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  name: text("name").notNull(),
  // 'daily' | 'weekly' | 'monthly'
  frequency: text("frequency").notNull(),
  // Hour-of-day in UTC when the schedule fires (0–23).
  hourUtc: integer("hour_utc").notNull().default(9),
  // For 'weekly': 0=Mon … 6=Sun (matches the standard ISO weekday but
  // 0-indexed for cleaner modulo math). Ignored for daily/monthly.
  dayOfWeek: integer("day_of_week"),
  // For 'monthly': 1–28 (capped at 28 so every month has the day).
  // Ignored for daily/weekly.
  dayOfMonth: integer("day_of_month"),
  // Relative window length — the export covers the [now-windowDays, now)
  // range each time it fires. "Last 7 days" / "Last 30 days" / etc.
  windowDays: integer("window_days").notNull().default(7),
  // Optional JSON of audit-handler filters: {chatbotId?, toolId?,
  // status?, integration?, userId?}. Re-validated on each run.
  filtersJson: text("filters_json"),
  // 'email' | 'storage'
  deliveryType: text("delivery_type").notNull(),
  // AES-256-GCM ciphertext envelope of the per-type config JSON. See
  // server/jobs/audit-export-scheduler.ts for the JSON shapes.
  deliveryConfigCipher: text("delivery_config_cipher").notNull(),
  isActive: text("is_active").default("true"),
  lastRunAt: timestamp("last_run_at"),
  lastStatus: text("last_status"),
  lastError: text("last_error"),
  lastRowCount: integer("last_row_count"),
  lastTruncated: text("last_truncated"),
  nextRunAt: timestamp("next_run_at"),
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const subscriptionPlans = pgTable("subscription_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  // Price in cents (USD).
  price: integer("price").notNull(),
  botLimit: integer("bot_limit").notNull(),
  agentLimit: integer("agent_limit").default(2),
  monthlyMessages: integer("monthly_messages").notNull(),
  features: text("features").array(),
  isActive: text("is_active").default("true"),
  // 'month' | 'year'. Drives the Stripe Price `recurring.interval`.
  billingInterval: text("billing_interval").default("month"),
  // Stripe Product / Price IDs created when the Master Admin saves a
  // plan with Stripe connected. A price is immutable in Stripe — when
  // price/interval change we create a NEW price, archive the old, and
  // update `stripePriceId` to the new one.
  stripeProductId: text("stripe_product_id"),
  stripePriceId: text("stripe_price_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const chatbots = pgTable("chatbots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull(),
  systemPrompt: text("system_prompt").default(""),
  welcomeMessage: text("welcome_message").default("Hello! How can I help you today?"),
  endMessage: text("end_message").default("Thank you for chatting with us! Please rate your experience."),
  scopeDescription: text("scope_description").default(""),
  model: text("model").default("gpt-4.1-mini"),
  temperature: integer("temperature").default(7),
  maxTokens: integer("max_tokens").default(2048),
  apiKey: text("api_key").notNull(),
  status: text("status").default("active"),
  primaryColor: text("primary_color").default("#0c3547"),
  theme: text("theme").default("professional-blue"),
  backgroundColor: text("background_color"),
  botBubbleColor: text("bot_bubble_color"),
  botTextColor: text("bot_text_color"),
  userBubbleColor: text("user_bubble_color"),
  userTextColor: text("user_text_color"),
  botIcon: text("bot_icon").default("bot"),
  botLogo: text("bot_logo"),
  allowedDomains: text("allowed_domains").array(),
  enabledChannels: text("enabled_channels").array().default(sql`ARRAY['web']::text[]`),
  smsWebhookUrl: text("sms_webhook_url"),
  emailWebhookUrl: text("email_webhook_url"),
  displayMode: text("display_mode").default("widget"),
  // Controls whether end-users must be signed in to chat with this bot.
  //   "authenticated" (default): caller must hold a valid session in the
  //     bot's organisation. Anonymous requests are rejected.
  //   "public": legacy behaviour — anyone with the apiKey can chat.
  // Internal staff bots default to authenticated so a leaked apiKey can't
  // be replayed against the bot from outside the org.
  accessMode: text("access_mode").default("authenticated"),
  supervisorEnabled: text("supervisor_enabled").default("false"),
  supervisorPrompt: text("supervisor_prompt").default(""),
  // Per-bot application of the org Institutional Voice profile:
  // "inherit" (default) | "extend" | "override". The voice* fields below are
  // only consulted for extend/override.
  voiceMode: text("voice_mode").default("inherit"),
  voiceTone: text("voice_tone"),
  voicePreferredTerms: text("voice_preferred_terms"),
  voiceProhibitedTerms: text("voice_prohibited_terms"),
  voiceReadingLevel: text("voice_reading_level"),
  voiceInclusivityStandard: text("voice_inclusivity_standard"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const knowledgeSources = pgTable("knowledge_sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  type: text("type").notNull(),
  name: text("name").notNull(),
  url: text("url"),
  content: text("content"),
  status: text("status").default("pending"),
  crawlDepth: integer("crawl_depth").default(1),
  maxPages: integer("max_pages").default(50),
  parentSourceId: varchar("parent_source_id"),
  config: text("config"),
  lastError: text("last_error"),
  itemCount: integer("item_count"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const predefinedQuestions = pgTable("predefined_questions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  question: text("question").notNull(),
  category: text("category").default("General"),
  sortOrder: integer("sort_order").default(0),
});

export const conversations = pgTable("conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  sessionId: text("session_id").notNull(),
  userName: text("user_name"),
  userEmail: text("user_email"),
  channel: text("channel").default("web"),
  status: text("status").default("active"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const conversationRatings = pgTable("conversation_ratings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  rating: integer("rating").notNull(),
  feedback: text("feedback"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull(),
  role: text("role").notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const knowledgeChunks = pgTable("knowledge_chunks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  knowledgeSourceId: varchar("knowledge_source_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  content: text("content").notNull(),
  chunkIndex: integer("chunk_index").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const settings = pgTable("settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  value: text("value").notNull(),
});

export const aiProviderKeys = pgTable("ai_provider_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"),
  organizationId: varchar("organization_id"),
  provider: text("provider").notNull(),
  apiKey: text("api_key").notNull(),
  detectedModels: text("detected_models"),
  isActive: text("is_active").default("true"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const webhooks = pgTable("webhooks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatbotId: varchar("chatbot_id").notNull(),
  url: text("url").notNull(),
  events: text("events").array().notNull(),
  secret: text("secret").notNull(),
  isActive: text("is_active").default("true"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const webhookDeliveries = pgTable("webhook_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  webhookId: varchar("webhook_id").notNull(),
  event: text("event").notNull(),
  payload: text("payload").notNull(),
  responseStatus: integer("response_status"),
  responseBody: text("response_body"),
  success: text("success").default("false"),
  createdAt: timestamp("created_at").defaultNow(),
});

// ─── LIVE CHAT ──────────────────────────────────────────────────────
// A "live chat session" is a hand-off from the AI bot to a human
// operator. The visitor's existing bot conversation continues to live
// in `conversations`/`messages`; this row tracks the queue/claim
// lifecycle and the new human-typed messages live in
// `live_chat_messages`.
export const liveChatSessions = pgTable("live_chat_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  chatbotId: varchar("chatbot_id").notNull(),
  // The visitor's bot session id (matches `conversations.session_id`),
  // so an operator can pull the pre-handoff bot transcript into their
  // panel for context.
  botSessionId: text("bot_session_id").notNull(),
  botConversationId: varchar("bot_conversation_id"),
  visitorUserId: varchar("visitor_user_id"),
  visitorName: text("visitor_name"),
  visitorEmail: text("visitor_email"),
  visitorIp: text("visitor_ip"),
  visitorUserAgent: text("visitor_user_agent"),
  visitorCountry: text("visitor_country"),
  // queued | active | escalated | closed | abandoned
  status: text("status").notNull().default("queued"),
  assignedOperatorId: varchar("assigned_operator_id"),
  reason: text("reason"),
  requestedAt: timestamp("requested_at").defaultNow(),
  claimedAt: timestamp("claimed_at"),
  closedAt: timestamp("closed_at"),
  lastVisitorAt: timestamp("last_visitor_at"),
  lastOperatorAt: timestamp("last_operator_at"),
  // Escalation-to-supervisor metadata.
  escalationReason: text("escalation_reason"),
  escalatedByUserId: varchar("escalated_by_user_id"),
  escalatedAt: timestamp("escalated_at"),
  // Wrap-up disposition captured on close.
  dispositionTag: text("disposition_tag"),
  dispositionOutcome: text("disposition_outcome"),
  dispositionNote: text("disposition_note"),
});

export const liveChatMessages = pgTable("live_chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull(),
  // visitor | operator | system
  sender: text("sender").notNull(),
  operatorId: varchar("operator_id"),
  content: text("content").notNull().default(""),
  // Optional file attachment: stored inline as a data URL for v1
  // (capped at 5 MB on the route). Production deployments can swap
  // this for an S3 reference without changing the message shape.
  attachmentUrl: text("attachment_url"),
  attachmentName: text("attachment_name"),
  attachmentMime: text("attachment_mime"),
  // Internal notes are operator-only and never shown to the visitor.
  internal: boolean("internal").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const cannedReplies = pgTable("canned_replies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const liveOperatorStatus = pgTable("live_operator_status", {
  userId: varchar("user_id").primaryKey(),
  organizationId: varchar("organization_id").notNull(),
  // online | away | offline
  status: text("status").notNull().default("offline"),
  // Optional out-of-office note shown to other operators.
  note: text("note"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const liveOfflineMessages = pgTable("live_offline_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").notNull(),
  chatbotId: varchar("chatbot_id"),
  name: text("name").notNull(),
  email: text("email").notNull(),
  message: text("message").notNull(),
  // new | handled
  status: text("status").notNull().default("new"),
  handledByUserId: varchar("handled_by_user_id"),
  handledAt: timestamp("handled_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id"),
  userId: varchar("user_id"),
  chatbotId: varchar("chatbot_id"),
  action: text("action").notNull(),
  resource: text("resource").notNull(),
  resourceId: varchar("resource_id"),
  details: text("details"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOrganizationSchema = createInsertSchema(organizations).omit({
  id: true,
  detectedModels: true,
  messagesUsed: true,
  createdAt: true,
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
});

export const insertSubscriptionPlanSchema = createInsertSchema(subscriptionPlans).omit({
  id: true,
  createdAt: true,
});

export const insertChatbotSchema = createInsertSchema(chatbots).omit({
  id: true,
  apiKey: true,
  createdAt: true,
});

export const insertKnowledgeSourceSchema = createInsertSchema(knowledgeSources).omit({
  id: true,
  content: true,
  status: true,
  createdAt: true,
});

export const insertPredefinedQuestionSchema = createInsertSchema(predefinedQuestions).omit({
  id: true,
});

export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  createdAt: true,
});

export const insertConversationRatingSchema = createInsertSchema(conversationRatings).omit({
  id: true,
  createdAt: true,
});

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});

export const insertKnowledgeChunkSchema = createInsertSchema(knowledgeChunks).omit({
  id: true,
  createdAt: true,
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});

export const insertWebhookSchema = createInsertSchema(webhooks).omit({
  id: true,
  createdAt: true,
});

export const insertWebhookDeliverySchema = createInsertSchema(webhookDeliveries).omit({
  id: true,
  createdAt: true,
});

export const insertSettingSchema = createInsertSchema(settings).omit({
  id: true,
});

export const insertAiProviderKeySchema = createInsertSchema(aiProviderKeys).omit({
  id: true,
  createdAt: true,
});

export const insertDepartmentSchema = createInsertSchema(departments).omit({ id: true, createdAt: true });
export const insertGroupSchema = createInsertSchema(groups).omit({ id: true, createdAt: true });
export const insertGroupMemberSchema = createInsertSchema(groupMembers).omit({ id: true, createdAt: true });
export const insertBotAccessRuleSchema = createInsertSchema(botAccessRules).omit({ id: true, updatedAt: true });
export const insertScimTokenSchema = createInsertSchema(scimTokens).omit({ id: true, createdAt: true, lastUsedAt: true });
export const insertChannelInstallationSchema = createInsertSchema(channelInstallations).omit({ id: true, createdAt: true });
export const insertToolSchema = createInsertSchema(tools).omit({ id: true, createdAt: true });
export const insertBotToolSchema = createInsertSchema(botTools).omit({ id: true, createdAt: true });
export const insertToolInvocationSchema = createInsertSchema(toolInvocations).omit({ id: true, createdAt: true });
export const insertApprovalRequestSchema = createInsertSchema(approvalRequests).omit({ id: true, createdAt: true, decidedAt: true });
export const insertIntegrationSchema = createInsertSchema(integrations).omit({ id: true, createdAt: true, lastCheckedAt: true });
export const insertScheduledAuditExportSchema = createInsertSchema(scheduledAuditExports).omit({
  id: true,
  createdAt: true,
  lastRunAt: true,
  lastStatus: true,
  lastError: true,
  lastRowCount: true,
  lastTruncated: true,
  nextRunAt: true,
});

export const insertLiveChatSessionSchema = createInsertSchema(liveChatSessions).omit({
  id: true,
  status: true,
  assignedOperatorId: true,
  requestedAt: true,
  claimedAt: true,
  closedAt: true,
  lastVisitorAt: true,
  lastOperatorAt: true,
});
export const insertLiveChatMessageSchema = createInsertSchema(liveChatMessages).omit({
  id: true,
  createdAt: true,
});
export const insertLiveOperatorStatusSchema = createInsertSchema(liveOperatorStatus).omit({
  updatedAt: true,
});
export const insertLiveOfflineMessageSchema = createInsertSchema(liveOfflineMessages).omit({
  id: true,
  status: true,
  handledByUserId: true,
  handledAt: true,
  createdAt: true,
});

export const ROLES = [
  "user",
  "admin",
  "super_admin",
  "employee",
  "manager",
  "hr_admin",
  "it_admin",
  "bot_admin",
  "org_admin",
  // Live-chat-only roles — these accounts ONLY see the Live Chat
  // section in the sidebar. They cannot author bots, manage
  // knowledge, or see the admin console.
  // - live_admin   : can configure live-chat settings, see all
  //                  operators' transcripts, manage offline forms
  // - live_power   : can claim queued sessions and see all
  //                  operators' active conversations (supervisor)
  // - live_agent   : front-line operator, claims & answers chats
  "live_admin",
  "live_power",
  "live_agent",
] as const;
export type Role = typeof ROLES[number];

// Roles that gate access to the Live Chat operator console. Centralise
// the membership check so server + client use the exact same set.
export const LIVE_CHAT_ROLES = new Set<string>([
  "live_admin",
  "live_power",
  "live_agent",
]);
export const LIVE_CHAT_ADMIN_ROLES = new Set<string>([
  "live_admin",
]);
export function isLiveChatRole(role?: string | null): boolean {
  return !!role && LIVE_CHAT_ROLES.has(role);
}
export function isLiveChatAdminRole(role?: string | null): boolean {
  return !!role && LIVE_CHAT_ADMIN_ROLES.has(role);
}

export type Webhook = typeof webhooks.$inferSelect;
export type InsertWebhook = z.infer<typeof insertWebhookSchema>;
export type WebhookDelivery = typeof webhookDeliveries.$inferSelect;
export type InsertWebhookDelivery = z.infer<typeof insertWebhookDeliverySchema>;
export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type SubscriptionPlan = typeof subscriptionPlans.$inferSelect;
export type InsertSubscriptionPlan = z.infer<typeof insertSubscriptionPlanSchema>;
export type Chatbot = typeof chatbots.$inferSelect;
export type InsertChatbot = z.infer<typeof insertChatbotSchema>;
export type KnowledgeSource = typeof knowledgeSources.$inferSelect;
export type InsertKnowledgeSource = z.infer<typeof insertKnowledgeSourceSchema>;
export type PredefinedQuestion = typeof predefinedQuestions.$inferSelect;
export type InsertPredefinedQuestion = z.infer<typeof insertPredefinedQuestionSchema>;
export type Conversation = typeof conversations.$inferSelect;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type ConversationRating = typeof conversationRatings.$inferSelect;
export type InsertConversationRating = z.infer<typeof insertConversationRatingSchema>;
export type Message = typeof messages.$inferSelect;
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type KnowledgeChunk = typeof knowledgeChunks.$inferSelect;
export type InsertKnowledgeChunk = z.infer<typeof insertKnowledgeChunkSchema>;
export type Setting = typeof settings.$inferSelect;
export type InsertSetting = z.infer<typeof insertSettingSchema>;
export type AiProviderKey = typeof aiProviderKeys.$inferSelect;
export type InsertAiProviderKey = z.infer<typeof insertAiProviderKeySchema>;
export type Department = typeof departments.$inferSelect;
export type InsertDepartment = z.infer<typeof insertDepartmentSchema>;
export type Group = typeof groups.$inferSelect;
export type InsertGroup = z.infer<typeof insertGroupSchema>;
export type GroupMember = typeof groupMembers.$inferSelect;
export type InsertGroupMember = z.infer<typeof insertGroupMemberSchema>;
export type BotAccessRule = typeof botAccessRules.$inferSelect;
export type InsertBotAccessRule = z.infer<typeof insertBotAccessRuleSchema>;
export type ScimToken = typeof scimTokens.$inferSelect;
export type InsertScimToken = z.infer<typeof insertScimTokenSchema>;
export type ChannelInstallation = typeof channelInstallations.$inferSelect;
export type InsertChannelInstallation = z.infer<typeof insertChannelInstallationSchema>;
export type Tool = typeof tools.$inferSelect;
export type InsertTool = z.infer<typeof insertToolSchema>;
export type BotTool = typeof botTools.$inferSelect;
export type InsertBotTool = z.infer<typeof insertBotToolSchema>;
export type ToolInvocation = typeof toolInvocations.$inferSelect;
export type InsertToolInvocation = z.infer<typeof insertToolInvocationSchema>;
export type ApprovalRequest = typeof approvalRequests.$inferSelect;
export type InsertApprovalRequest = z.infer<typeof insertApprovalRequestSchema>;
export type Integration = typeof integrations.$inferSelect;
export type InsertIntegration = z.infer<typeof insertIntegrationSchema>;
export type ScheduledAuditExport = typeof scheduledAuditExports.$inferSelect;
export type InsertScheduledAuditExport = z.infer<typeof insertScheduledAuditExportSchema>;
export type LiveChatSession = typeof liveChatSessions.$inferSelect;
export type InsertLiveChatSession = z.infer<typeof insertLiveChatSessionSchema>;
export type LiveChatMessage = typeof liveChatMessages.$inferSelect;
export type InsertLiveChatMessage = z.infer<typeof insertLiveChatMessageSchema>;
export type CannedReply = typeof cannedReplies.$inferSelect;
export type LiveOperatorStatus = typeof liveOperatorStatus.$inferSelect;
export type InsertLiveOperatorStatus = z.infer<typeof insertLiveOperatorStatusSchema>;
export type LiveOfflineMessage = typeof liveOfflineMessages.$inferSelect;
export type InsertLiveOfflineMessage = z.infer<typeof insertLiveOfflineMessageSchema>;
